import { useSelector } from "react-redux"
import { RootState } from "../../../app/store"
import { useAppDispatch } from "../../../app/hooks"
import {
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material"
import DialogHandlePrize from "./dialog-handle-prize"
import {
  clearPrizeFormAct,
  sendPrizeRaffleThunk,
  setPrizeForImagsAct,
  setPrizeFormRaffleAct,
} from "../handle-raffle.slice"
import { Add, Edit, EmojiEvents, Photo } from "@mui/icons-material"
import { numberToCurrency } from "../../../utils/numbers.utils"
import DialogHandlePrizeImgs from "./dialog-handle-prize-imgs"

const denseCell = { py: 0.75, px: 1.25, fontSize: "0.8125rem" }

export default function RafflePrizesHandler({ embedded = false }: { embedded?: boolean }) {
  const { currentRaffle, prizeForm, prizeForImgs } = useSelector((state: RootState) => state.raffle)
  const dispatch = useAppDispatch()

  if (currentRaffle === undefined) return null

  const prizes = currentRaffle.prizes ?? []
  const totalCost = currentRaffle.cost ?? 0

  return (
    <>
      <DialogHandlePrizeImgs
        show={prizeForImgs !== undefined}
        prize={prizeForImgs}
        onClose={() => dispatch(setPrizeForImagsAct(undefined))}
      />
      {prizeForm !== undefined && (
        <DialogHandlePrize
          show={prizeForm !== undefined}
          prize={prizeForm}
          onClose={() => dispatch(clearPrizeFormAct())}
          onConfirm={() => dispatch(sendPrizeRaffleThunk({ prize: prizeForm! }))}
        />
      )}

      <Stack spacing={2}>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "stretch", sm: "center" },
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Box>
            <Stack direction="row" alignItems="center" spacing={1}>
              <EmojiEvents color="primary" fontSize="small" />
              <Typography variant="subtitle1" fontWeight={600}>
                Premios del sorteo
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary">
              Define los premios y sube fotos para cada uno.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip
              size="small"
              label={`Costo total: $ ${numberToCurrency(totalCost)}`}
              color="primary"
              variant="outlined"
            />
            <Button
              variant="contained"
              size="small"
              startIcon={<Add />}
              onClick={() =>
                dispatch(
                  setPrizeFormRaffleAct({
                    description: "",
                    images: [],
                    name: "",
                    raffle: currentRaffle._id!,
                    price: "",
                  }),
                )
              }
              sx={{ cursor: "pointer", whiteSpace: "nowrap" }}
            >
              Agregar premio
            </Button>
          </Stack>
        </Box>

        {prizes.length === 0 ? (
          <Box
            sx={{
              border: "2px dashed",
              borderColor: "divider",
              borderRadius: 2,
              py: 5,
              px: 2,
              textAlign: "center",
              bgcolor: embedded ? "action.hover" : "transparent",
            }}
          >
            <EmojiEvents sx={{ fontSize: 40, color: "text.disabled", mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              Agrega al menos un premio para mostrar el valor del sorteo.
            </Typography>
          </Box>
        ) : (
          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1.5 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "action.hover" }}>
                  <TableCell sx={{ ...denseCell, fontWeight: 600 }}>Premio</TableCell>
                  <TableCell sx={{ ...denseCell, fontWeight: 600 }} align="right">
                    Valor
                  </TableCell>
                  <TableCell sx={{ ...denseCell, fontWeight: 600 }} align="center">
                    Fotos
                  </TableCell>
                  <TableCell sx={{ ...denseCell, fontWeight: 600 }} align="right">
                    Acciones
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {prizes.map((el) => (
                  <TableRow key={el._id} hover sx={{ "&:last-child td": { border: 0 } }}>
                    <TableCell sx={denseCell}>
                      <Typography variant="body2" fontWeight={500}>
                        {el.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 240, display: "block" }}>
                        {el.description}
                      </Typography>
                    </TableCell>
                    <TableCell sx={denseCell} align="right">
                      $ {numberToCurrency(el.price)}
                    </TableCell>
                    <TableCell sx={denseCell} align="center">
                      <Chip
                        size="small"
                        label={el.images?.length ?? 0}
                        variant="outlined"
                        sx={{ height: 22, minWidth: 32 }}
                      />
                    </TableCell>
                    <TableCell sx={denseCell} align="right">
                      <Tooltip title="Fotos del premio">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => dispatch(setPrizeForImagsAct(el))}
                          sx={{ cursor: "pointer", p: 0.5 }}
                        >
                          <Photo sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Editar premio">
                        <IconButton
                          size="small"
                          color="secondary"
                          onClick={() => dispatch(setPrizeFormRaffleAct(el))}
                          sx={{ cursor: "pointer", p: 0.5 }}
                        >
                          <Edit sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Stack>
    </>
  )
}
